import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from "~/components/ui/sheet";
import { Button } from "~/components/ui/button";
import { CartItem, useBookingFlowStore } from "~/stores/booking-flow-store";
import { useShallow } from "zustand/react/shallow";
import { Gift } from "lucide-react";
import { Language, useLanguageStore } from "~/stores/language-store";
import { useTailwindBreakpoint } from "~/hooks/use-tailwind-breakpoint";
import { BiTrashAlt } from "react-icons/bi";

interface CartDrawerProps {
    open: boolean;
    onClose: () => void;
    disableRemoveCartItems?: boolean;
    cartItems?: CartItem[];
    onCartItemsChange?: (items: CartItem[]) => void;
}

// Helper to get total price for a cart item (including add-ons)
const getCartItemTotalPrice = (item: CartItem): number => {
    const addOnsTotal = item.addOns?.reduce((sum, addon) => sum + addon.price, 0) ?? 0;
    return item.price + addOnsTotal;
};

// Helper to get total duration for a cart item (including add-ons)
const getCartItemTotalDuration = (item: CartItem): number => {
    const addOnsDuration = item.addOns?.reduce((sum, addon) => sum + addon.durationMinutes, 0) ?? 0;
    return item.durationMinutes + addOnsDuration;
};

const CartDrawer = ({ open, onClose, disableRemoveCartItems, cartItems: cartItemsProp, onCartItemsChange }: CartDrawerProps) => {
    const size = useTailwindBreakpoint();

    const { cartItems: storeCartItems, setCartItems: setStoreCartItems } = useBookingFlowStore(
        useShallow((state) => ({
            cartItems: state.cartItems,
            setCartItems: state.setCartItems,
        }))
    );
    const cartItems = cartItemsProp ?? storeCartItems;
    const setCartItems = onCartItemsChange ?? setStoreCartItems;

    const totalAmount = cartItems.reduce((sum, item) => sum + (item.isBundleRedemption ? 0 : getCartItemTotalPrice(item)), 0);
    const removeItem = (bookableItemId: string, selectedVariantId: string) => {
        if (disableRemoveCartItems === true) {
            return;
        }
        // Remove item matching both bookableItemId and selectedVariantId
        setCartItems(cartItems.filter(item => 
            !(item.bookableItemId === bookableItemId && item.selectedVariantId === selectedVariantId)
        ));
    };
    const { t, language } = useLanguageStore();

    const formatDuration = (minutes: number) => {
        if (minutes < 60) {
            return `${minutes} Min.`;
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        if (remainingMinutes > 0) {
            return language === Language.GERMAN
                ? `${hours} Std. ${remainingMinutes} Min.`
                : `${hours} h ${remainingMinutes} min`;
        }
        return language === Language.GERMAN ? `${hours} Std.` : `${hours} h`;
    };

    const formatPrice = (price: number) =>
        new Intl.NumberFormat('de-DE', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(price);

    return (
        <Sheet open={open} onOpenChange={onClose}>
            <SheetContent side={size === 'xs' || size === 'sm' ? 'bottom' : 'right'} className="rounded-t-2xl p-0 flex flex-col h-[50vh] md:h-screen">
                <div className="border-b">
                    <SheetHeader>
                        <SheetTitle className="text-left text-burgundy">{t('yourTreatments')}</SheetTitle>
                    </SheetHeader>
                </div>
                
                {/* Cart Items - Scrollable middle section */}
                <div className="flex-1 overflow-y-auto px-4 py-2">
                    <div className="space-y-4">
                        {cartItems.map((item, index) => {
                            const itemName = language === Language.GERMAN 
                                ? item.bookableItemNameDe 
                                : item.bookableItemNameEn;
                            const optionLabel = language === Language.GERMAN
                                ? item.selectedVariantOptionLabelDe
                                : item.selectedVariantOptionLabelEn;

                            const isRedemption = !!item.isBundleRedemption;
                            const isBundle = item.bookableItemType === 'BUNDLE' && !!item.bundleId && !isRedemption;
                            const itemTotalPrice = getCartItemTotalPrice(item);
                            
                            return (
                                <div
                                    key={`${item.bookableItemId}-${item.selectedVariantId}-${index}`}
                                    className="flex items-center justify-between px-4 py-2 bg-light-burgundy rounded-lg"
                                >
                                    <div className="flex-1 pr-4">
                                        <h3 className="font-medium text-burgundy mb-1 flex items-center gap-1.5">
                                            {(isBundle || isRedemption) && (
                                                <Gift className="w-4 h-4 text-burgundy shrink-0" />
                                            )}
                                            <span>
                                                {itemName}
                                                {optionLabel && (
                                                    <span className="text-burgundy/60 font-normal ml-1">
                                                        ({optionLabel})
                                                    </span>
                                                )}
                                            </span>
                                        </h3>
                                        {isBundle && (
                                            <div className="text-xs text-burgundy/60 mb-1">
                                                {language === Language.GERMAN ? 'Bundle' : 'Bundle'}
                                                {item.bundleUnitQuantity && item.bundleUnitPriceGross && (
                                                    <span> ({item.bundleUnitQuantity} × {formatPrice(item.bundleUnitPriceGross)} EUR {language === Language.GERMAN ? 'pro Sitzung' : 'per session'})</span>
                                                )}
                                            </div>
                                        )}
                                        {isRedemption && (
                                            <div className="text-xs text-burgundy/60 mb-1">
                                                {language === Language.GERMAN ? 'Bundle-Einlösung' : 'Bundle redemption'}
                                            </div>
                                        )}
                                        {item.addOns && item.addOns.length > 0 && (
                                            <div className="text-xs text-burgundy/60 mb-1">
                                                + {item.addOns.map(addon => 
                                                    language === Language.GERMAN ? addon.addOnNameDe : addon.addOnNameEn
                                                ).join(', ')}
                                            </div>
                                        )}
                                        <div className="flex items-center gap-2 text-sm text-burgundy">
                                            <span>{formatDuration(getCartItemTotalDuration(item))}</span>
                                            <div className="w-[3px] h-[3px] rounded-full bg-burgundy/40" />
                                            <span>{isRedemption ? '0 EUR' : `${language === Language.GERMAN ? 'ab ' : 'from '}${formatPrice(itemTotalPrice)} EUR`}</span>
                                        </div>
                                    </div>
                                    {disableRemoveCartItems !== true && (
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => removeItem(item.bookableItemId, item.selectedVariantId)}
                                            className="text-burgundy hover:bg-red-50 p-2"
                                        >
                                            <BiTrashAlt className="h-5! w-5!" />
                                        </Button>
                                    )}
                                </div>
                            );
                        })}
                        {cartItems.length === 0 && (
                            <div className="text-center py-8 text-gray-500">
                                <p>{t('emptyCart')}</p>
                                <p className="text-sm mt-1">{t('addTreatmentsToGetStarted')}</p>
                            </div>
                        )}
                    </div>
                </div>
                
                {/* Total Summary - Fixed at bottom */}
                {cartItems.length > 0 && (
                    <div className="border-t pb-8 bg-white">
                        <SheetFooter>
                            <div className="flex justify-between items-center w-full">
                                <p className="text-lg font-semibold text-burgundy">{t('totalPrice')}</p>
                                <p className="text-lg font-semibold text-burgundy">{language === Language.GERMAN ? 'ab ' : 'from '}{formatPrice(totalAmount)} EUR</p>
                            </div>
                        </SheetFooter>
                    </div>
                )}
            </SheetContent>
        </Sheet>
    );
};

export default CartDrawer;